import { useEffect, useRef, useState } from 'react';
import usePrefersReducedMotion from './usePrefersReducedMotion';

// Reveal-once primitive: returns [ref, isVisible]. Content is visible by
// default when motion is reduced or IntersectionObserver is unavailable.
function useInView({ threshold = 0.15, rootMargin = '0px 0px -10% 0px' } = {}) {
  const targetRef = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion || typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsVisible(true);
      return undefined;
    }

    const target = targetRef.current;
    if (!target) {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [prefersReducedMotion, rootMargin, threshold]);

  return [targetRef, isVisible];
}

export default useInView;
